import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { BentoFeature } from '../../types/premium';
import ScrollReveal from './ScrollReveal';
import './BentoFeatureGrid.css';

type BentoAudience = 'cro' | 'sites' | 'patients';

interface BentoFeatureGridProps {
  audience: BentoAudience;
  eyebrow: string;
  title: string;
  subtitle: string;
  features: BentoFeature[];
}

const audienceLabel: Record<BentoAudience, string> = {
  cro: 'Built for CRO leadership and delivery teams',
  sites: 'Built for sites and clinical operations',
  patients: 'Built for patients and families',
};

const BentoFeatureGrid: React.FC<BentoFeatureGridProps> = ({
  audience,
  eyebrow,
  title,
  subtitle,
  features,
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      className={`bento-section bento-section--${audience}`}
      aria-labelledby="bento-section-title"
      data-bento-audience={audience}
    >
      <ScrollReveal className="bento-section__header">
        <span className="bento-section__eyebrow">{eyebrow}</span>
        <h2 id="bento-section-title" className="bento-section__title">
          {title}
        </h2>
        <p className="bento-section__subtitle">{subtitle}</p>
        <p className="bento-section__audience-line">{audienceLabel[audience]}</p>
      </ScrollReveal>
      <div className="bento-grid">
        {features.map((feature, index) => {
          const spanClass =
            feature.span === 'wide'
              ? 'bento-card--wide'
              : feature.span === 'tall'
                ? 'bento-card--tall'
                : '';
          const idx = String(index + 1).padStart(2, '0');
          return (
            <motion.article
              key={feature.id}
              className={`bento-card ${spanClass}`.trim()}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{
                delay: index * 0.05,
                duration: prefersReducedMotion ? 0.35 : 0.5,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={prefersReducedMotion ? undefined : { y: -3 }}
            >
              <div className="bento-card__rail" aria-hidden />
              <div className="bento-card__main">
                <div className="bento-card__meta">
                  <span className="bento-card__index">{idx}</span>
                  <span className="bento-card__facet">{feature.tag}</span>
                </div>
                <h3 className="bento-card__title">{feature.title}</h3>
                <p className="bento-card__desc">{feature.description}</p>
              </div>
              <p className="bento-card__outcome">{feature.outcome}</p>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
};

export default BentoFeatureGrid;
